import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import apiClient from '../../services/api/apiClient';
import { useToast } from '../ui/Toast/Toast';

/**
 * NotificationPanel - 顯示 Webhook 通知
 * 當收到回覆、提及等通知時顯示
 */
function NotificationPanel({ accountId, onClose }) {
  const { toast } = useToast();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  // Load notifications on mount and when accountId changes
  useEffect(() => {
    if (accountId) {
      loadNotifications();
      loadUnreadCount();

      // Poll for new notifications every 30 seconds
      const interval = setInterval(() => {
        loadNotifications();
        loadUnreadCount();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [accountId]);

  // Load notifications from API
  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/webhooks/notifications', {
        params: {
          accountId,
          limit: 50,
          status: 'pending' // Only show unread notifications
        }
      });

      if (response.data.success) {
        setNotifications(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load unread count
  const loadUnreadCount = async () => {
    try {
      const response = await apiClient.get('/webhooks/notifications/unread-count', {
        params: { accountId }
      });

      if (response.data.success) {
        setUnreadCount(response.data.data.count);
      }
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await apiClient.put(`/webhooks/notifications/${notificationId}/read`);

      // Update local state
      setNotifications(notifications.filter(n => n.id !== notificationId));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      toast.error('Failed to mark notification as read');
    }
  };

  // Get icon for notification type
  const getNotificationIcon = (field) => {
    switch (field) {
      case 'replies':
        return { icon: 'mdi:reply', color: 'text-blue-500' };
      case 'mentions':
        return { icon: 'mdi:at', color: 'text-purple-500' };
      case 'delete':
        return { icon: 'mdi:delete', color: 'text-red-500' };
      case 'publish':
        return { icon: 'mdi:check-circle', color: 'text-green-500' };
      default:
        return { icon: 'mdi:bell', color: 'text-gray-500' };
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="fixed right-0 top-0 h-screen w-96 bg-white shadow-2xl z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <Icon icon="mdi:bell" width={24} className="text-blue-600" />
          <h2 className="text-xl font-bold text-gray-900">Notifications</h2>
          {unreadCount > 0 && (
            <span className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <Icon icon="mdi:close" width={24} className="text-gray-500" />
        </button>
      </div>

      {/* Notifications List */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Icon icon="mdi:loading" width={48} className="text-blue-500 animate-spin" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center px-6">
            <Icon icon="mdi:bell-outline" width={64} className="text-gray-300 mb-4" />
            <p className="text-gray-600 text-lg font-medium">All caught up!</p>
            <p className="text-gray-500 text-sm mt-2">No new notifications</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {notifications.map((notification) => {
              const { icon, color } = getNotificationIcon(notification.field);

              return (
                <div
                  key={notification.id}
                  className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => markAsRead(notification.id)}
                >
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center ${color}`}>
                      <Icon icon={icon} width={20} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {/* Username & Time */}
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-gray-900 text-sm truncate">
                          @{notification.reply_username}
                        </span>
                        <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                          {formatTime(notification.created_at)}
                        </span>
                      </div>

                      {/* Type Label */}
                      <div className="mb-2">
                        {notification.field === 'replies' && (
                          <span className="text-xs text-blue-600 font-medium">replied to your post</span>
                        )}
                        {notification.field === 'mentions' && (
                          <span className="text-xs text-purple-600 font-medium">mentioned you</span>
                        )}
                      </div>

                      {/* Reply Text */}
                      {notification.reply_text && (
                        <p className="text-sm text-gray-700 line-clamp-3 mb-2">
                          {notification.reply_text}
                        </p>
                      )}

                      {/* Action Button */}
                      <a
                        href={notification.reply_permalink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        View on Threads
                        <Icon icon="mdi:open-in-new" width={12} />
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200">
        <button
          onClick={loadNotifications}
          className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
        >
          <Icon icon="mdi:refresh" width={18} />
          Refresh
        </button>
      </div>
    </div>
  );
}

export default NotificationPanel;
